import { z } from "zod";
import type { TransactionListItem } from "@common/schemas/transaction.schema";
import {
    ColumnOptionsSchema,
    ColumnSchema,
    ViewSchema,
    ViewType,
} from "./views";

export enum TransactionColumn {
    DATE = "date",
    AMOUNT = "amount",
    ORIGINAL_AMOUNT = "original_amount",
    DESCRIPTION = "description",
    BANK_DESCRIPTION = "bank_description",
    CATEGORY = "category",
    ACCOUNT = "account",
    SUBACCOUNT = "subaccount",
    COUNTERPARTY = "counterparty",
    CUSTOMER = "customer",
    NOTE = "note",
    RECEIPTS = "receipts",
    SPLIT = "split",
    CLASSES = "classes",
    ENTITY = "entity",
    REVIEW = "review",
    TRANSFER_MATCH = "transfer_match",
    MATCHING_ACTION = "matching_action",
    LOCK = "lock",
    LEDGER = "ledger",
    ACCRUAL_LEDGER = "accrual_ledger",
    CTA = "CTA",
    RECONCILIATION_STATUS = "reconciliation_status",
    ACCRUAL_ACTIONS = "accrual_actions",
    ACCRUAL_REVIEW_LEDGER = "accrual_review_ledger",
    ACCRUAL_MATCH = "accrual_match",
    SCHEDULE = "schedule",
    SCHEDULE_REVIEW_ACTIONS = "schedule_review_actions",
    NEW_VALUE = "__newValue__",
    EXCLUDE_FROM_1099 = "exclude_from_1099",
    BILLABLE_EXPENSE_INVOICE_STATUS = "billable_expense_invoice_status",
}

export function isTransactionColumn(
    columnId: string,
): columnId is TransactionColumn {
    return Object.values(TransactionColumn).some(
        (column) => column === columnId,
    );
}

const DateColumnSchema = ColumnSchema.extend({
    id: z.literal(TransactionColumn.DATE),
});
export type DateColumn = z.infer<typeof DateColumnSchema>;

const AmountColumnSchema = ColumnSchema.extend({
    id: z.literal(TransactionColumn.AMOUNT),
});
export type AmountColumn = z.infer<typeof AmountColumnSchema>;

const OriginalAmountColumnSchema = ColumnSchema.extend({
    id: z.literal(TransactionColumn.ORIGINAL_AMOUNT),
});
export type OriginalAmountColumn = z.infer<typeof OriginalAmountColumnSchema>;

const DescriptionColumnSchema = ColumnSchema.extend({
    id: z.literal(TransactionColumn.DESCRIPTION),
});
export type DescriptionColumn = z.infer<typeof DescriptionColumnSchema>;

const BankDescriptionColumnSchema = ColumnSchema.extend({
    id: z.literal(TransactionColumn.BANK_DESCRIPTION),
});
export type BankDescriptionColumn = z.infer<typeof BankDescriptionColumnSchema>;

/** Controls which sub-features are shown inside the CategoryCell. */
export const CategoryCellVariantSchema = z.enum([
    "full",
    "category-only",
    "review",
]);
export type CategoryCellVariant = z.infer<typeof CategoryCellVariantSchema>;

/** Ledger column display variant. "review" renders a review-mode scaffold. */
export const LedgerCellVariantSchema = z.enum(["standard", "review"]);
export type LedgerCellVariant = z.infer<typeof LedgerCellVariantSchema>;

/**
 * Display variant shared by review-capable cell columns (counterparty, entity).
 * "review" is only ever injected by review task views (`injectReviewColumns`) —
 * it is not user-selectable. Reused by both cells so the enum values stay in sync.
 */
export const ReviewCellVariantSchema = z.enum(["full", "review"]);
export type ReviewCellVariant = z.infer<typeof ReviewCellVariantSchema>;

export const CounterpartyCellVariantSchema = ReviewCellVariantSchema;
export type CounterpartyCellVariant = ReviewCellVariant;

export const EntityCellVariantSchema = ReviewCellVariantSchema;
export type EntityCellVariant = ReviewCellVariant;

/**
 * Column `meta` shape read by review-capable cells to resolve their display
 * variant. Shared so the cell components stay structurally in sync.
 */
export const ReviewColumnMetaSchema = z.object({
    variant: z.string().optional(),
    customVariants: z.record(z.string(), z.string()).optional(),
});

const CategoryColumnSchema = ColumnSchema.extend({
    id: z.literal(TransactionColumn.CATEGORY),
    options: ColumnOptionsSchema.extend({
        required: z.boolean().optional(),
        /**
         * Controls the visual variant of the CategoryCell:
         * - "full"          — category picker + transfer-match link (default)
         * - "category-only" — category picker only; hides the match button
         */
        variant: CategoryCellVariantSchema.optional(),
    }).optional(),
});
export type CategoryColumn = z.infer<typeof CategoryColumnSchema>;

const AccountColumnSchema = ColumnSchema.extend({
    id: z.literal(TransactionColumn.ACCOUNT),
});
export type AccountColumn = z.infer<typeof AccountColumnSchema>;

const CounterpartyColumnSchema = ColumnSchema.extend({
    id: z.literal(TransactionColumn.COUNTERPARTY),
});
export type CounterpartyColumn = z.infer<typeof CounterpartyColumnSchema>;

const CustomerColumnSchema = ColumnSchema.extend({
    id: z.literal(TransactionColumn.CUSTOMER),
});
export type CustomerColumn = z.infer<typeof CustomerColumnSchema>;

const NoteColumnSchema = ColumnSchema.extend({
    id: z.literal(TransactionColumn.NOTE),
});
export type NoteColumn = z.infer<typeof NoteColumnSchema>;

const ReceiptsColumnSchema = ColumnSchema.extend({
    id: z.literal(TransactionColumn.RECEIPTS),
});
export type ReceiptsColumn = z.infer<typeof ReceiptsColumnSchema>;

const SplitColumnSchema = ColumnSchema.extend({
    id: z.literal(TransactionColumn.SPLIT),
});
export type SplitColumn = z.infer<typeof SplitColumnSchema>;

const ClassesColumnSchema = ColumnSchema.extend({
    id: z.literal(TransactionColumn.CLASSES),
});
const LedgerSchema = ColumnSchema.extend({
    id: z.literal(TransactionColumn.LEDGER),
    options: ColumnOptionsSchema.extend({
        variant: LedgerCellVariantSchema.optional(),
    }).optional(),
});
const AccrualLedgerSchema = ColumnSchema.extend({
    id: z.literal(TransactionColumn.ACCRUAL_LEDGER),
});
export type ClassesColumn = z.infer<typeof ClassesColumnSchema>;

const EntityColumnSchema = ColumnSchema.extend({
    id: z.literal(TransactionColumn.ENTITY),
});
export type EntityColumn = z.infer<typeof EntityColumnSchema>;

const TransferMatchColumnSchema = ColumnSchema.extend({
    id: z.literal(TransactionColumn.TRANSFER_MATCH),
});
export type TransferMatchColumn = z.infer<typeof TransferMatchColumnSchema>;

const MatchingActionColumnSchema = ColumnSchema.extend({
    id: z.literal(TransactionColumn.MATCHING_ACTION),
});
export type MatchingActionColumn = z.infer<typeof MatchingActionColumnSchema>;

const LockColumnSchema = ColumnSchema.extend({
    id: z.literal(TransactionColumn.LOCK),
});
export type LockColumn = z.infer<typeof LockColumnSchema>;

const SubaccountColumnSchema = ColumnSchema.extend({
    id: z.literal(TransactionColumn.SUBACCOUNT),
});
export type SubaccountColumn = z.infer<typeof SubaccountColumnSchema>;

const transactionListItemArgSchema: z.ZodType<TransactionListItem> =
    z.custom<TransactionListItem>();

const TransactionCTAColumnSchema = ColumnSchema.extend({
    id: z.literal(TransactionColumn.CTA),
    options: ColumnOptionsSchema.extend({
        label: z.union([
            z.string(),
            z.function().args(transactionListItemArgSchema).returns(z.string()),
        ]),
        tooltip: z
            .union([
                z.string(),
                z
                    .function()
                    .args(transactionListItemArgSchema)
                    .returns(z.union([z.string(), z.undefined()])),
            ])
            .optional(),
        isDisabled: z
            .function()
            .args(transactionListItemArgSchema)
            .returns(z.boolean())
            .optional(),
        onClick: z
            .function()
            .args(transactionListItemArgSchema)
            .returns(z.union([z.void(), z.promise(z.void())])),
    }),
});
export type TransactionCTAColumn = z.infer<typeof TransactionCTAColumnSchema>;

const ReconciliationStatusColumnSchema = ColumnSchema.extend({
    id: z.literal(TransactionColumn.RECONCILIATION_STATUS),
});
export type ReconciliationStatusColumn = z.infer<
    typeof ReconciliationStatusColumnSchema
>;

const AccrualActionsColumnSchema = ColumnSchema.extend({
    id: z.literal(TransactionColumn.ACCRUAL_ACTIONS),
});
export type AccrualActionsColumn = z.infer<typeof AccrualActionsColumnSchema>;

const AccrualReviewLedgerColumnSchema = ColumnSchema.extend({
    id: z.literal(TransactionColumn.ACCRUAL_REVIEW_LEDGER),
});
export type AccrualReviewLedgerColumn = z.infer<
    typeof AccrualReviewLedgerColumnSchema
>;

const AccrualMatchColumnSchema = ColumnSchema.extend({
    id: z.literal(TransactionColumn.ACCRUAL_MATCH),
});
export type AccrualMatchColumn = z.infer<typeof AccrualMatchColumnSchema>;

const ScheduleColumnSchema = ColumnSchema.extend({
    id: z.literal(TransactionColumn.SCHEDULE),
});
export type ScheduleColumn = z.infer<typeof ScheduleColumnSchema>;

const ScheduleReviewActionsColumnSchema = ColumnSchema.extend({
    id: z.literal(TransactionColumn.SCHEDULE_REVIEW_ACTIONS),
});
export type ScheduleReviewActionsColumn = z.infer<
    typeof ScheduleReviewActionsColumnSchema
>;

const NewValueColumnSchema = ColumnSchema.extend({
    id: z.literal(TransactionColumn.NEW_VALUE),
});
export type NewValueColumn = z.infer<typeof NewValueColumnSchema>;

const ExcludeFrom1099ColumnSchema = ColumnSchema.extend({
    id: z.literal(TransactionColumn.EXCLUDE_FROM_1099),
});
export type ExcludeFrom1099Column = z.infer<typeof ExcludeFrom1099ColumnSchema>;

const BillableExpenseInvoiceStatusColumnSchema = ColumnSchema.extend({
    id: z.literal(TransactionColumn.BILLABLE_EXPENSE_INVOICE_STATUS),
});
export type BillableExpenseInvoiceStatusColumn = z.infer<
    typeof BillableExpenseInvoiceStatusColumnSchema
>;

const DynamicLedgerAccountColumnSchema = ColumnSchema.extend({
    id: z.union([
        z.string().startsWith("ledgerAccount:"),
        z.string().startsWith("suggestedLedger:"),
    ]),
});

/**
 * Fallback for column ids that match neither a known literal nor a dynamic
 * prefix. The column is preserved as-is so the whole view still parses, but we
 * emit a warning so the unrecognised id is observable (consuming code skips
 * columns with no matching config, otherwise the column would silently vanish).
 */
const UnknownColumnFallbackSchema = ColumnSchema.transform((column) => {
    // eslint-disable-next-line no-console -- common/ has no shared logger; warn so unknown column ids stay observable in FE & BE logs
    console.warn(
        `[TransactionViewSchema] Unrecognised column id "${column.id}" preserved via fallback schema.`,
    );
    return column;
});

export const TransactionViewSchema = ViewSchema.extend({
    type: z.literal(ViewType.TRANSACTION),
    columns: z.array(
        z.union([
            z.discriminatedUnion("id", [
                DateColumnSchema,
                AmountColumnSchema,
                OriginalAmountColumnSchema,
                DescriptionColumnSchema,
                BankDescriptionColumnSchema,
                CategoryColumnSchema,
                AccountColumnSchema,
                CounterpartyColumnSchema,
                CustomerColumnSchema,
                NoteColumnSchema,
                ReceiptsColumnSchema,
                SplitColumnSchema,
                ClassesColumnSchema,
                EntityColumnSchema,
                TransferMatchColumnSchema,
                MatchingActionColumnSchema,
                ColumnSchema.extend({
                    id: z.literal(TransactionColumn.REVIEW),
                }),
                LockColumnSchema,
                LedgerSchema,
                AccrualLedgerSchema,
                TransactionCTAColumnSchema,
                ReconciliationStatusColumnSchema,
                AccrualActionsColumnSchema,
                AccrualReviewLedgerColumnSchema,
                AccrualMatchColumnSchema,
                ScheduleColumnSchema,
                ScheduleReviewActionsColumnSchema,
                SubaccountColumnSchema,
                NewValueColumnSchema,
                ExcludeFrom1099ColumnSchema,
                BillableExpenseInvoiceStatusColumnSchema,
            ]),
            DynamicLedgerAccountColumnSchema,
            UnknownColumnFallbackSchema,
        ]),
    ),
});

export type TransactionView = z.infer<typeof TransactionViewSchema>;
export type TransactionViewOptions = z.infer<
    typeof TransactionViewSchema
>["options"];
